import { useState } from 'react';
import { parseISO, differenceInMinutes } from 'date-fns';
import { Calendar, Clock, ShoppingCart } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import Select from '@/components/form/Select';
import Error from '@/components/common/Error';
import { currencyFormat } from '@/helpers/stringHelpers';
import Spinner from '@/components/common/Spinner';
import { usePostCart, useAddItemToCart, useCart } from '@/data/cart';
import { useCartCookie } from '@/helpers/useCartCookie';
import { useCartIsOpen } from '@/app/cart/useCartIsOpen';
import { useAvailabilityContext } from '@/app/availability/useAvailabilityContext';
import {
  getDateWithTimeZone,
  getTimeWithTimeZone
} from '@/helpers/dateHelpers';
import { useInvalidateAvailability } from '@/data/availability';

const formSchema = z.object({
  locationId: z.string().min(1, { message: 'Location is required' })
});

const SelectAvailability = ({ availability, coachActivity }) => {
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const postCart = usePostCart();
  const addItemToCart = useAddItemToCart();
  const { setCartId, getCartId } = useCartCookie();
  const { openCart } = useCartIsOpen();
  const cartId = getCartId();
  const { mutate: mutateCart } = useCart(cartId);
  const { closeAvailability } = useAvailabilityContext();
  const invalidateAll = useInvalidateAvailability();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      locationId: availability.locations[0]?.locationId || ''
    }
  });

  const onSubmit = async (values) => {
    try {
      setSubmitting(true);
      let newCartId = cartId;

      if (!cartId) {
        const newCart = await postCart({
          cartItems: [
            {
              availabilityId: availability.id,
              locationId: values.locationId,
              startTime: getTimeWithTimeZone(
                availability.startDateTime,
                availability.timeZone,
                'HH:mm'
              ),
              endTime: getTimeWithTimeZone(
                availability.endDateTime,
                availability.timeZone,
                'HH:mm'
              )
            }
          ]
        });
        newCartId = newCart.id;
        setCartId(newCartId);
      } else {
        await addItemToCart(cartId, {
          availabilityId: availability.id,
          locationId: values.locationId,
          startTime: getTimeWithTimeZone(
            availability.startDateTime,
            availability.timeZone,
            'HH:mm'
          ),
          endTime: getTimeWithTimeZone(
            availability.endDateTime,
            availability.timeZone,
            'HH:mm'
          )
        });
      }

      mutateCart && mutateCart();
      await invalidateAll();
      openCart();
      closeAvailability();
    } catch (err) {
      setApiError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const locationOptions = availability.locations.map((loc) => ({
    label: loc.location.name,
    value: loc.locationId
  }));

  const durationInMinutes = differenceInMinutes(
    parseISO(availability.endDateTime),
    parseISO(availability.startDateTime)
  );
  const durationInHours = durationInMinutes / 60;
  const subTotal = coachActivity.hourlyPrice * durationInHours;

  return (
    <Card className="w-full">
      <CardHeader className="space-y-4">
        <div className="space-y-2">
          <CardTitle className="text-lg font-semibold">
            {availability.activity.title}
          </CardTitle>

          <div className="space-y-1.5">
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>
                {getDateWithTimeZone(
                  availability.startDateTime,
                  availability.timeZone
                )}
              </span>
            </div>
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>
                {getTimeWithTimeZone(
                  availability.startDateTime,
                  availability.timeZone
                )}{' '}
                -{' '}
                {getTimeWithTimeZone(
                  availability.endDateTime,
                  availability.timeZone
                )}
              </span>
            </div>
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <Select
              name="locationId"
              label="Location"
              options={locationOptions}
            />
            <div className="flex justify-between items-end">
              <div>
                <div className="text-sm text-muted-foreground">
                  {currencyFormat(coachActivity.hourlyPrice)}/hr ×{' '}
                  {durationInHours} hrs
                </div>
                <div className="text-lg font-medium">
                  {currencyFormat(subTotal)}
                </div>
              </div>
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <Spinner className="mr-2 h-4 w-4" />
                ) : (
                  <ShoppingCart className="mr-2 h-4 w-4" />
                )}
                Add to Cart
              </Button>
            </div>
            {apiError && <Error error={apiError} />}
          </form>
        </Form>
      </CardHeader>
    </Card>
  );
};

export default SelectAvailability;
